/**
 * The attach-a-payment-screenshot control on the public pay pages.
 *
 * Manual (QR / bank transfer) mode only: the member pays the club directly,
 * then attaches the screenshot from their UPI or banking app so the committee
 * can match the transfer. Optional by design — not every app hands one over,
 * and a missing screenshot must never block a real payment. The image is
 * resized on the canvas (PROOF_IMAGE keeps it readable) and rides with the
 * confirm call; only its URL ever lands on the payment row.
 */

import { useState } from 'react';
import { pickAndResizePhoto, PROOF_IMAGE } from '../lib/image';

export function ProofPicker({
  proof,
  onChange,
  /* Who reads it — the club's committee by default; the platform owner on
     the portal's subscription desk. */
  audience = 'the committee',
  title = 'Paid already? Attach the screenshot',
}) {
  const [problem, setProblem] = useState(null);

  async function pick() {
    setProblem(null);
    try {
      onChange(await pickAndResizePhoto(PROOF_IMAGE));
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not read that image');
    }
  }

  return (
    <div className="mt-4 border border-dashed border-line-strong bg-ground p-3 text-left">
      {proof ? (
        <div className="flex items-center gap-3">
          <img
            src={proof}
            alt="Your payment screenshot"
            className="h-[72px] w-[54px] flex-none border border-line bg-white object-cover"
          />
          <div className="min-w-0 flex-1">
            <p className="font-market text-[12px] font-bold text-ink-700">Screenshot attached</p>
            <p className="text-[11px] leading-snug text-copy-muted">
              Goes to {audience} with your payment.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="flex-none border border-line-strong bg-white px-2.5 py-1.5 font-market text-[10px] uppercase tracking-wide text-copy-muted hover:border-ink-700 hover:text-ink-700"
          >
            Remove
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-market text-[12px] font-bold text-ink-700">{title}</p>
            <p className="text-[11px] leading-snug text-copy-muted">
              From your UPI or banking app — it helps {audience} match your
              transfer.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void pick()}
            className="flex-none border border-line-strong bg-white px-3 py-2 font-market text-[10.5px] font-bold uppercase tracking-wide text-ink-700 hover:border-ink-700"
          >
            Attach
          </button>
        </div>
      )}
      {problem && (
        <p className="mt-2 font-market text-[11px] text-flag-600">{problem}</p>
      )}
    </div>
  );
}
